import type { ServiceHandler } from "../bridge/message-router";

/**
 * 右键菜单项在宿主侧的运行时表示：归属插件、展示文案与可选图标，以及根据当前选区是否显示。
 */
export interface PluginContextMenuItem {
  pluginId: string;
  id: string;
  title: string;
  icon?: string;
  when: "always" | "hasSelection" | "singleSelection";
}

const registeredMenuItems = new Map<string, PluginContextMenuItem[]>();

/**
 * 聚合所有插件注册的菜单项，供宿主渲染上下文菜单时一次性拉平使用。
 */
export function getPluginContextMenuItems(): PluginContextMenuItem[] {
  const all: PluginContextMenuItem[] = [];
  for (const items of registeredMenuItems.values()) {
    all.push(...items);
  }
  return all;
}

/**
 * 返回指定插件当前仍生效的菜单项列表（未注册则空数组）。
 */
export function getPluginMenuItemsForPlugin(pluginId: string): PluginContextMenuItem[] {
  return registeredMenuItems.get(pluginId) ?? [];
}

/**
 * 在插件卸载或需重置时移除该插件贡献的全部右键项，避免残留引用。
 */
export function clearPluginContextMenuItems(pluginId: string): void {
  registeredMenuItems.delete(pluginId);
}

/**
 * 上下文菜单服务：维护「插件 id → 菜单项数组」的内存注册表，支持增删改后与宿主 UI 同步。
 */
export const contextMenuServiceHandler: ServiceHandler = async (pluginId, method, params) => {
  switch (method) {
    case "create": {
      /* 向该插件条目列表追加一项，可按选区条件（始终 / 有选中 / 单选）控制可见性 */
      const { id, title, icon, when = "always" } = params as {
        id: string;
        title: string;
        icon?: string;
        when?: "always" | "hasSelection" | "singleSelection";
      };
      if (!registeredMenuItems.has(pluginId)) {
        registeredMenuItems.set(pluginId, []);
      }
      registeredMenuItems.get(pluginId)!.push({ pluginId, id, title, icon, when });
      return;
    }

    case "update": {
      /* 按业务 id 找到已注册项并合并局部字段（标题、图标、显示条件等） */
      const { id, changes } = params as {
        id: string;
        changes: Partial<Omit<PluginContextMenuItem, "id" | "pluginId">>;
      };
      const items = registeredMenuItems.get(pluginId);
      if (items) {
        const item = items.find((i) => i.id === id);
        if (item) Object.assign(item, changes);
      }
      return;
    }

    case "remove": {
      /* 从该插件列表中移除指定 id，不影响其它插件 */
      const { id } = params as { id: string };
      const items = registeredMenuItems.get(pluginId);
      if (items) {
        const idx = items.findIndex((i) => i.id === id);
        if (idx !== -1) items.splice(idx, 1);
      }
      return;
    }

    default:
      throw Object.assign(new Error(`Unknown contextMenu method: ${method}`), { code: "UNKNOWN_METHOD" });
  }
};
